import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, Shield, Clock, Users, Award } from "lucide-react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

export const AboutPreview: React.FC = () => {
  const { t } = useTranslation();
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6 },
    },
  };

  const features = [
    {
      icon: Shield,
      title: t("1000", "Safe & Secure"),
      description: t("1001", "Professional drivers with years of experience"),
    },
    {
      icon: Clock,
      title: t("1002", "Always On Time"),
      description: t("1003", "Punctual service you can depend on"),
    },
    {
      icon: Users,
      title: t("1004", "Customer First"),
      description: t("1005", "Dedicated to exceptional service"),
    },
    {
      icon: Award,
      title: t("1006", "Premium Quality"),
      description: t("1007", "Mercedes-Benz fleet with latest technology"),
    },
  ];

  return (
    <section className="py-24 relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(circle_at_30%_20%,var(--primary)_0%,transparent_50%)]" />
        <div className="absolute bottom-0 right-0 w-full h-full bg-[radial-gradient(circle_at_70%_80%,var(--accent)_0%,transparent_50%)]" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center"
        >
          {/* Left Content */}
          <motion.div variants={itemVariants} className="space-y-8">
            <div className="space-y-4">
              <motion.div
                variants={itemVariants}
                className="inline-flex items-center space-x-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium"
              >
                <Award className="h-4 w-4" />
                <span>{t("1008", "About TaxiSchipholAirport")}</span>
              </motion.div>

              <motion.h2
                variants={itemVariants}
                className="text-4xl md:text-5xl font-bold text-white leading-tight"
              >
                {t("1009", "Your Reliable")}{" "}
                <span className="relative">
                  <span className="bg-linear-to-r from-primary to-primary-light bg-clip-text text-transparent">
                    {t("1010", "Transportation")}
                  </span>
                  <motion.div
                    className="absolute -bottom-1 left-0 right-0 h-1 bg-linear-to-r from-primary to-primary-light rounded-full"
                    initial={{ scaleX: 0 }}
                    whileInView={{ scaleX: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8 }}
                  />
                </span>{" "}
                {t("1011", "Solution")}
              </motion.h2>

              <motion.p
                variants={itemVariants}
                className="text-lg text-gray-100 leading-relaxed"
              >
                {t(
                  "1012",
                  "Welcome to TaxiSchipholAirport, your reliable and efficient transportation solution for travel to and from Schiphol Airport."
                )}
              </motion.p>
            </div>

            <motion.div variants={itemVariants} className="space-y-6">
              <p className="text-gray-200 leading-relaxed">
                {t(
                  "1013",
                  "At TaxiSchipholAirport, we pride ourselves on providing our customers with safe, comfortable, and timely transportation services. Our team of experienced and professional drivers is dedicated to ensuring that you arrive at your destination on time and in style."
                )}
              </p>

              <p className="text-gray-200 leading-relaxed">
                {t(
                  "1014",
                  "Whether you're traveling for business or pleasure, we're here to make your journey as smooth and stress-free as possible. We offer a range of transportation options to suit your needs, including airport transfers, taxi services, and corporate transportation."
                )}
              </p>
            </motion.div>

            {/* Feature Grid */}
            <motion.div
              variants={itemVariants}
              className="grid grid-cols-2 gap-4"
            >
              {features.map((feature) => (
                <motion.div
                  key={feature.title}
                  whileHover={{ scale: 1.02 }}
                  className="bg-gray-800/50 backdrop-blur-xs p-4 rounded-xl shadow-xs border border-gray-700 hover:shadow-md transition-all duration-300"
                >
                  <feature.icon className="h-8 w-8 text-primary mb-2" />
                  <div className="font-semibold text-white text-sm mb-1">
                    {feature.title}
                  </div>
                  <p className="text-xs text-gray-300 leading-relaxed">
                    {feature.description}
                  </p>
                </motion.div>
              ))}
            </motion.div>

            {/* CTA Button */}
            <motion.div variants={itemVariants}>
              <Link
                to="/about"
                className="group inline-flex items-center bg-linear-to-r from-primary to-primary-light hover:from-primary-dark hover:to-primary text-white px-8 py-4 rounded-xl font-semibold transition-all duration-300 transform hover:scale-105 hover:shadow-xl"
              >
                <span>{t("1015", "Learn More About Us")}</span>
                <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </Link>
            </motion.div>
          </motion.div>

          {/* Right Content - Image */}
          <motion.div variants={itemVariants} className="relative">
            <motion.div
              className="relative group"
              whileHover={{ scale: 1.02 }}
              transition={{ duration: 0.3 }}
            >
              {/* Main Image */}
              <div className="relative overflow-hidden rounded-3xl shadow-2xl">
                <img
                  src="/images/about.webp"
                  alt={t("1016", "Professional taxi driver")}
                  className="w-full h-[500px] object-cover transform group-hover:scale-110 transition-transform duration-700"
                />

                {/* Gradient Overlay */}
                <div className="absolute inset-0 bg-linear-to-t from-gray-900/60 via-transparent to-transparent" />

                {/* Floating Badge */}
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6 }}
                  className="absolute bottom-6 left-6 bg-gray-800/95 backdrop-blur-xs p-4 rounded-2xl shadow-xl"
                >
                  <div className="flex items-center space-x-3">
                    <div className="flex -space-x-2">
                      <div className="w-8 h-8 bg-primary rounded-full border-2 border-white flex items-center justify-center">
                        <Shield className="h-4 w-4 text-white" />
                      </div>
                      <div className="w-8 h-8 bg-accent rounded-full border-2 border-white flex items-center justify-center">
                        <Award className="h-4 w-4 text-white" />
                      </div>
                    </div>
                    <div>
                      <div className="font-bold text-white text-sm">
                        {t("1017", "Trusted Service")}
                      </div>
                      <div className="text-xs text-gray-300">
                        {t("1018", "Since 2020")}
                      </div>
                    </div>
                  </div>
                </motion.div>
              </div>

              {/* Decorative Elements */}
              <motion.div
                className="absolute -top-6 -right-6 w-24 h-24 bg-linear-to-r from-primary/20 to-accent/20 rounded-full blur-xl"
                animate={{
                  scale: [1, 1.2, 1],
                  opacity: [0.3, 0.6, 0.3],
                }}
                transition={{
                  duration: 4,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              />

              <motion.div
                className="absolute -bottom-6 -left-6 w-32 h-32 bg-linear-to-r from-accent/20 to-primary/20 rounded-full blur-xl"
                animate={{
                  scale: [1.2, 1, 1.2],
                  opacity: [0.4, 0.7, 0.4],
                }}
                transition={{
                  duration: 5,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              />
            </motion.div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};
